import {
  ABILITIES, ABILITY_NAMES, SKILLS, type Ability, type SkillKey,
} from '@shared/rules/constants'
import { abilityModifier, formatModifier } from '@shared/rules/calc'
import {
  POINT_BUY_BUDGET, POINT_BUY_COST, STANDARD_ARRAY, finalScores, poolLeftovers,
  pointBuySpent, rollAbilityPool,
  type AbilityMethod, type CharacterDraft,
} from '@shared/rules/build'
import { CLASS_BUILDS_BY_ID } from '@shared/rules/data/classes'
import { SPECIES_BY_ID } from '@shared/rules/data/species'
import { BACKGROUNDS_BY_ID } from '@shared/rules/data/backgrounds'
import { Badge, Button, NumberField, Panel, Select } from '@/components/ui'
import { cn } from '@/lib/cn'
import { ChoiceCard, Explainer, PickChip, PickCounter, SectionLabel, StepIntro } from './shared'
import type { StepProps } from './StepIdentity'

/** What each ability actually does, in one line, for people new to the game. */
const ABILITY_HELP: Record<Ability, string> = {
  str: 'Hitting things hard, lifting, shoving, climbing.',
  dex: 'Aim, reflexes, sneaking, and your Armor Class.',
  con: 'Hit points and staying conscious. Nobody dumps this.',
  int: 'Knowledge, investigation, and Wizard magic.',
  wis: 'Noticing things, reading people, and Cleric and Druid magic.',
  cha: 'Persuasion, deception, and Bard, Paladin, Sorcerer, and Warlock magic.',
}

// ---------- Ability scores ----------

export function StepAbilities({ draft, update }: StepProps) {
  const classBuild = draft.classId ? CLASS_BUILDS_BY_ID.get(draft.classId) : null
  const background = BACKGROUNDS_BY_ID.get(draft.backgroundId)
  const totals = finalScores(draft)
  const spent = pointBuySpent(draft.baseScores)

  // Standard array and rolled scores both draw from a fixed pool of six numbers.
  const pool =
    draft.abilityMethod === 'standard' ? [...STANDARD_ARRAY]
    : draft.abilityMethod === 'roll' && draft.rolledPool.length ? draft.rolledPool
    : null
  const leftovers = pool ? poolLeftovers(pool, ABILITIES.map((a) => draft.baseScores[a])) : null

  function setMethod(method: AbilityMethod) {
    update({
      abilityMethod: method,
      rolledPool: [],
      baseScores:
        method === 'pointbuy'
          ? { str: 8, dex: 8, con: 8, int: 8, wis: 8, cha: 8 }
          : { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
    })
  }

  /** Rolls a fresh pool and drops it straight onto the class's best abilities. */
  function rollScores() {
    const pool = rollAbilityPool()
    update({ rolledPool: pool, baseScores: assignInOrder(pool, classBuild?.primaryAbility ?? []) })
  }

  function autoAssign() {
    if (!classBuild) return
    update({ baseScores: assignInOrder(STANDARD_ARRAY, classBuild.primaryAbility) })
  }

  return (
    <div>
      <StepIntro title="Your six ability scores">
        These six numbers describe what your character is naturally good at. What matters is the
        modifier next to each one — that is the number you actually add to your dice rolls.
      </StepIntro>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <ChoiceCard
          selected={draft.abilityMethod === 'standard'}
          onSelect={() => setMethod('standard')}
          title="Standard array"
          tagline="Recommended. Everyone gets the same six numbers: 15, 14, 13, 12, 10, 8."
        />
        <ChoiceCard
          selected={draft.abilityMethod === 'pointbuy'}
          onSelect={() => setMethod('pointbuy')}
          title="Point buy"
          tagline="Spend 27 points to build your own spread. More control, more fiddly."
        />
        <ChoiceCard
          selected={draft.abilityMethod === 'roll'}
          onSelect={() => setMethod('roll')}
          title="Roll the dice"
          tagline="Roll 4d6 and drop the lowest, six times. Swingy — you might get great scores, or not."
        />
        <ChoiceCard
          selected={draft.abilityMethod === 'manual'}
          onSelect={() => setMethod('manual')}
          title="Enter them myself"
          tagline="Use this if you already rolled at the table and just need to type the numbers in."
        />
      </div>

      <Panel className="p-4">
        <SectionLabel
          action={
            <div className="flex items-center gap-2">
              {draft.abilityMethod === 'pointbuy' && (
                <Badge tone={spent > POINT_BUY_BUDGET ? 'blood' : spent === POINT_BUY_BUDGET ? 'moss' : 'neutral'}>
                  {spent} / {POINT_BUY_BUDGET} points
                </Badge>
              )}
              {leftovers && (
                <Badge tone={leftovers.missing.length ? 'gold' : 'moss'}>
                  {leftovers.missing.length ? `to place: ${leftovers.missing.join(', ')}` : 'all placed'}
                </Badge>
              )}
              {draft.abilityMethod === 'standard' && classBuild && (
                <Button size="sm" onClick={autoAssign}>Assign for a {classBuild.name}</Button>
              )}
              {draft.abilityMethod === 'roll' && (
                <Button size="sm" variant={draft.rolledPool.length ? 'secondary' : 'primary'} onClick={rollScores}>
                  {draft.rolledPool.length ? 'Roll again' : 'Roll 4d6 × 6'}
                </Button>
              )}
            </div>
          }
        >
          {draft.abilityMethod === 'pointbuy' || draft.abilityMethod === 'manual'
            ? 'Set your scores'
            : 'Assign your numbers'}
        </SectionLabel>

        {draft.abilityMethod === 'roll' && (
          <div className="mb-4">
            {draft.rolledPool.length === 0 ? (
              <p className="text-sm text-ink-400">
                Hit <span className="text-gold-400">Roll 4d6 × 6</span> to generate six scores. Each
                one rolls four six-sided dice and throws away the lowest, which is why the numbers
                skew high. Re-roll as many times as your DM allows.
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-ink-500">You rolled</span>
                {draft.rolledPool.map((value, index) => (
                  <span
                    key={`${value}-${index}`}
                    className="rounded-lg border border-gold-600/40 bg-gold-900/30 px-2.5 py-1 font-mono text-sm tabular-nums text-gold-400"
                  >
                    {value}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          {ABILITIES.map((ability) => (
            <AbilityRow
              key={ability}
              ability={ability}
              draft={draft}
              update={update}
              total={totals[ability]}
              pool={pool}
              primary={classBuild?.primaryAbility.includes(ability) ?? false}
            />
          ))}
        </div>

        {leftovers && leftovers.missing.length > 0 && (
          <p className="mt-3 text-xs text-ink-500">
            Every number has to be used exactly once. Still to place:{' '}
            <span className="font-mono text-gold-400">{leftovers.missing.join(', ')}</span>
          </p>
        )}
      </Panel>

      {background && (
        <BackgroundIncreases
          draft={draft}
          update={update}
          abilities={background.abilities}
          name={background.name}
          keyAbilities={classBuild?.primaryAbility ?? []}
          className={classBuild?.name ?? ''}
        />
      )}

      <div className="mt-5">
        <Explainer term="modifier">
          A score of 10 or 11 gives a modifier of +0 — completely average. Every 2 points above that
          adds +1, and every 2 below subtracts 1. So a 16 gives +3, and an 8 gives −1. When you roll
          a twenty-sided die for something, you add the relevant modifier to the result.
        </Explainer>
      </div>
    </div>
  )
}

function AbilityRow({
  ability, draft, update, total, pool, primary,
}: {
  ability: Ability
  draft: CharacterDraft
  update: (patch: Partial<CharacterDraft>) => void
  total: number
  /** Fixed set of six numbers to place, for the array and rolled methods. */
  pool: number[] | null
  primary: boolean
}) {
  const base = draft.baseScores[ability]
  const bonus = draft.backgroundIncreases[ability] ?? 0

  // Flag a value used more often than the pool actually contains it. Counting
  // rather than checking uniqueness matters because rolls repeat: two 13s in
  // the pool means two abilities may legitimately hold 13.
  const overused = pool !== null && (() => {
    const allowed = pool.filter((v) => v === base).length
    const used = ABILITIES.filter((a) => draft.baseScores[a] === base).length
    return used > allowed
  })()

  const setBase = (value: number) => update({ baseScores: { ...draft.baseScores, [ability]: value } })

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-ink-700/60 bg-ink-900/40 px-3 py-2.5">
      <div className="w-32 shrink-0">
        <p className={cn('text-sm font-medium', primary ? 'text-gold-400' : 'text-ink-100')}>
          {ABILITY_NAMES[ability]}
          {primary && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-gold-600">key</span>}
        </p>
        <p className="text-[11px] leading-tight text-ink-500">{ABILITY_HELP[ability]}</p>
      </div>

      <div className="shrink-0">
        {pool ? (
          <Select
            aria-label={`${ABILITY_NAMES[ability]} score`}
            value={base}
            onChange={(e) => setBase(Number(e.target.value))}
            className={cn('h-9 w-20', overused && 'border-blood-500/60')}
          >
            {[...new Set([base, ...pool])].sort((a, b) => b - a).map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </Select>
        ) : draft.abilityMethod === 'pointbuy' ? (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="secondary" aria-label={`Lower ${ABILITY_NAMES[ability]}`}
              disabled={base <= 8} onClick={() => setBase(base - 1)}>−</Button>
            <span className="w-8 text-center font-mono text-sm tabular-nums">{base}</span>
            <Button size="sm" variant="secondary" aria-label={`Raise ${ABILITY_NAMES[ability]}`}
              disabled={base >= 15 || pointBuySpent(draft.baseScores) + costStep(base) > POINT_BUY_BUDGET}
              onClick={() => setBase(base + 1)}>+</Button>
          </div>
        ) : (
          <NumberField
            min={3}
            max={18}
            fallback={10}
            value={base}
            aria-label={`${ABILITY_NAMES[ability]} score`}
            onChange={setBase}
            className="h-9 w-20"
          />
        )}
      </div>

      <div className="ml-auto flex items-center gap-3 text-right">
        {bonus > 0 && <span className="font-mono text-xs text-moss-500">+{bonus}</span>}
        <div className="w-14">
          <p className="font-mono text-lg leading-none tabular-nums text-ink-100">{total}</p>
          <p className="text-[10px] uppercase tracking-wider text-ink-500">score</p>
        </div>
        <div className="w-12">
          <p className="font-mono text-lg leading-none tabular-nums text-gold-400">
            {formatModifier(abilityModifier(total))}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-ink-500">mod</p>
        </div>
      </div>
    </div>
  )
}

const costStep = (from: number): number => (POINT_BUY_COST[from + 1] ?? 99) - (POINT_BUY_COST[from] ?? 0)

/**
 * Drops a pool of scores onto abilities highest-first, starting with the class's
 * key abilities and then Constitution — a sane default the player can rearrange.
 */
function assignInOrder(pool: readonly number[], primary: readonly Ability[]): Record<Ability, number> {
  const order: Ability[] = [
    ...primary,
    'con',
    ...ABILITIES.filter((a) => a !== 'con' && !primary.includes(a)),
  ]
  const sorted = [...pool].sort((a, b) => b - a)
  const scores = { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 } as Record<Ability, number>
  order.forEach((ability, index) => {
    const value = sorted[index]
    if (value !== undefined) scores[ability] = value
  })
  return scores
}

function BackgroundIncreases({
  draft, update, abilities, name, keyAbilities, className,
}: {
  draft: CharacterDraft
  update: (patch: Partial<CharacterDraft>) => void
  abilities: [Ability, Ability, Ability]
  name: string
  keyAbilities: readonly Ability[]
  className: string
}) {
  const increases = draft.backgroundIncreases
  const total = Object.values(increases).reduce((a, b) => a + b, 0)
  const spread = Object.values(increases).includes(2) ? 'two-one' : total === 3 ? 'one-each' : ''

  // Default the +2 to whatever this class actually uses, when the background offers it.
  const preferred = abilities.find((a) => keyAbilities.includes(a)) ?? abilities[0]
  const missesKeyAbility = keyAbilities.length > 0 && !keyAbilities.some((a) => abilities.includes(a))

  const setSpread = (mode: 'two-one' | 'one-each') => {
    if (mode === 'one-each') {
      update({ backgroundIncreases: Object.fromEntries(abilities.map((a) => [a, 1])) })
    } else {
      const second = abilities.find((a) => a !== preferred)!
      update({ backgroundIncreases: { [preferred]: 2, [second]: 1 } })
    }
  }

  return (
    <Panel className="mt-5 p-4">
      <SectionLabel action={<PickCounter chosen={total} total={3} noun="points" />}>
        {name} ability increases
      </SectionLabel>
      <p className="mb-4 text-sm text-ink-400">
        Your background raises three specific abilities. Put +2 into one and +1 into another, or
        spread +1 across all three.
      </p>

      {missesKeyAbility && (
        <div className="mb-4 rounded-xl border border-gold-600/40 bg-gold-900/25 p-3 text-sm leading-relaxed text-ink-300">
          Heads up: {className}s rely on{' '}
          <span className="text-gold-400">
            {keyAbilities.map((a) => ABILITY_NAMES[a]).join(' or ')}
          </span>
          , which {name} does not raise. That is perfectly playable — plenty of characters are built
          this way — but if you would rather have a higher one, go back and pick a background that
          offers it.
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <ChoiceCard
          selected={spread === 'two-one'}
          onSelect={() => setSpread('two-one')}
          title="+2 and +1"
          tagline="Recommended — get one ability as high as possible."
        />
        <ChoiceCard
          selected={spread === 'one-each'}
          onSelect={() => setSpread('one-each')}
          title="+1 to all three"
          tagline="A flatter, more well-rounded character."
        />
      </div>

      {spread === 'two-one' && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1.5 block text-xs uppercase tracking-wider text-ink-400">Gets +2</span>
            <Select
              value={abilities.find((a) => increases[a] === 2) ?? ''}
              onChange={(e) => {
                const two = e.target.value as Ability
                const one = abilities.find((a) => a !== two && increases[a] === 1)
                  ?? abilities.find((a) => a !== two)!
                update({ backgroundIncreases: { [two]: 2, [one]: 1 } })
              }}
            >
              {abilities.map((a) => <option key={a} value={a}>{ABILITY_NAMES[a]}</option>)}
            </Select>
          </label>
          <label className="text-sm">
            <span className="mb-1.5 block text-xs uppercase tracking-wider text-ink-400">Gets +1</span>
            <Select
              value={abilities.find((a) => increases[a] === 1) ?? ''}
              onChange={(e) => {
                const one = e.target.value as Ability
                const two = abilities.find((a) => a !== one && increases[a] === 2)
                  ?? abilities.find((a) => a !== one)!
                update({ backgroundIncreases: { [two]: 2, [one]: 1 } })
              }}
            >
              {abilities.filter((a) => increases[a] !== 2).map((a) => (
                <option key={a} value={a}>{ABILITY_NAMES[a]}</option>
              ))}
            </Select>
          </label>
        </div>
      )}
    </Panel>
  )
}

// ---------- Skills ----------

export function StepSkills({ draft, update }: StepProps) {
  const classBuild = draft.classId ? CLASS_BUILDS_BY_ID.get(draft.classId) : null
  const species = SPECIES_BY_ID.get(draft.speciesId)
  const background = BACKGROUNDS_BY_ID.get(draft.backgroundId)
  // Built inline rather than memoised: it must not sit behind the early return
  // below, and rebuilding a six-entry map costs nothing.
  const granted = new Map<SkillKey, string>()
  if (background) {
    for (const skill of background.skills) granted.set(skill, background.name)
  }
  if (species) {
    for (const skill of (species.skillProficiencies ?? []) as SkillKey[]) granted.set(skill, species.name)
  }

  if (!classBuild || !species || !background) return null

  const expertiseChoice = classBuild.choices.find((c) => c.id === 'expertise')
  const proficient = [...granted.keys(), ...draft.classSkills, ...draft.speciesSkills]

  const toggle = (list: SkillKey[], key: SkillKey, max: number): SkillKey[] =>
    list.includes(key) ? list.filter((s) => s !== key) : list.length < max ? [...list, key] : list

  return (
    <div className="space-y-6">
      <StepIntro title="What are you good at?">
        Being proficient in a skill means you add your proficiency bonus — +2 at this level — every
        time you roll for it. Some skills come free from your background and species; the rest you
        choose here.
      </StepIntro>

      {granted.size > 0 && (
        <Panel className="p-4">
          <SectionLabel>Already yours</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {[...granted.entries()].map(([skill, source]) => (
              <span key={skill} className="rounded-lg border border-moss-500/30 bg-moss-900/40 px-2.5 py-1.5 text-sm text-moss-500">
                {SKILLS[skill].name}
                <span className="ml-1.5 text-[11px] text-ink-500">from {source}</span>
              </span>
            ))}
          </div>
        </Panel>
      )}

      <Panel className="p-4">
        <SectionLabel action={<PickCounter chosen={draft.classSkills.length} total={classBuild.skillChoices.count} noun="chosen" />}>
          {classBuild.name} skills
        </SectionLabel>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {classBuild.skillChoices.from.map((skill) => {
            const alreadyGranted = granted.has(skill)
            return (
              <PickChip
                key={skill}
                label={SKILLS[skill].name}
                meta={SKILLS[skill].ability}
                help={alreadyGranted ? 'Already proficient' : undefined}
                selected={draft.classSkills.includes(skill)}
                disabled={alreadyGranted || draft.classSkills.length >= classBuild.skillChoices.count}
                onToggle={() => update({ classSkills: toggle(draft.classSkills, skill, classBuild.skillChoices.count) })}
              />
            )
          })}
        </div>
      </Panel>

      {(species.grantsSkillChoice ?? 0) > 0 && (
        <Panel className="p-4">
          <SectionLabel action={<PickCounter chosen={draft.speciesSkills.length} total={species.grantsSkillChoice!} noun="chosen" />}>
            {species.name} skill
          </SectionLabel>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {((species.skillChoiceFrom as SkillKey[] | undefined) ?? (Object.keys(SKILLS) as SkillKey[])).map((skill) => (
              <PickChip
                key={skill}
                label={SKILLS[skill].name}
                meta={SKILLS[skill].ability}
                selected={draft.speciesSkills.includes(skill)}
                disabled={granted.has(skill) || draft.classSkills.includes(skill) || draft.speciesSkills.length >= species.grantsSkillChoice!}
                onToggle={() => update({ speciesSkills: toggle(draft.speciesSkills, skill, species.grantsSkillChoice!) })}
              />
            ))}
          </div>
        </Panel>
      )}

      {expertiseChoice && (
        <Panel className="p-4">
          <SectionLabel action={<PickCounter chosen={draft.expertise.length} total={expertiseChoice.count} noun="chosen" />}>
            Expertise
          </SectionLabel>
          <p className="mb-3 text-sm text-ink-400">{expertiseChoice.help}</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {proficient.map((skill) => (
              <PickChip
                key={skill}
                label={SKILLS[skill].name}
                meta="double bonus"
                selected={draft.expertise.includes(skill)}
                disabled={draft.expertise.length >= expertiseChoice.count}
                onToggle={() => update({ expertise: toggle(draft.expertise, skill, expertiseChoice.count) })}
              />
            ))}
          </div>
          {proficient.length === 0 && (
            <p className="text-sm text-ink-500">Choose your class skills above first.</p>
          )}
        </Panel>
      )}
    </div>
  )
}
